import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { downloadDocument, fmtErr } from "@/lib/api";
import { useProject } from "@/context/ProjectContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor, RichTextView } from "@/components/RichTextEditor";
import { ActivityTimeline } from "@/components/ActivityTimeline";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge, PriorityBadge, RFI_PRIORITIES, fmtDate, fmtDateTime } from "@/components/badges";
import { CheckCircle2, Download, Eye, Loader2, MessageSquareReply, Pencil, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "@/components/ConfirmDialog";

export default function Rfis() {
  const { askConfirm, confirmDialog } = useConfirm();
  const { selectedId } = useProject();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [viewRfi, setViewRfi] = useState(null);
  const [answerRfi, setAnswerRfi] = useState(null);
  const [form, setForm] = useState({ to_party_id: "", subject: "", question: "", priority: "Medium", due_date: "" });
  const [answerForm, setAnswerForm] = useState({ answer: "", file: null });
  const [editRfi, setEditRfi] = useState(null);
  const [editRfiForm, setEditRfiForm] = useState({ subject: "", question: "", priority: "Medium", due_date: "" });
  const [closeRfiTarget, setCloseRfiTarget] = useState(null);
  const [closeReason, setCloseReason] = useState("");
  const [createFile, setCreateFile] = useState(null);

  const { data: rfis = [], isLoading } = useQuery({
    queryKey: ["rfis", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/rfis`)).data,
    enabled: !!selectedId,
  });
  const { data: parties = [] } = useQuery({
    queryKey: ["parties", selectedId],
    queryFn: async () => (await api.get(`/projects/${selectedId}/parties`)).data,
    enabled: !!selectedId,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["rfis", selectedId] });
    qc.invalidateQueries({ queryKey: ["dashboard", selectedId] });
  };

  const createMut = useMutation({
    mutationFn: async () => (await api.post(`/projects/${selectedId}/rfis`, { ...form, due_date: form.due_date || null })).data,
    onSuccess: async (d) => {
      toast.success(t("rfis.toastCreated", { no: d.rfi_no }));
      if (createFile) {
        const fd = new FormData();
        fd.append("file", createFile);
        try {
          await api.post(`/rfis/${d.id}/attachment`, fd);
        } catch (e) {
          toast.error(fmtErr(e));
        }
      }
      setCreateOpen(false);
      setForm({ to_party_id: "", subject: "", question: "", priority: "Medium", due_date: "" });
      setCreateFile(null);
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const answerMut = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("answer", answerForm.answer);
      if (answerForm.file) fd.append("file", answerForm.file);
      return (await api.post(`/rfis/${answerRfi.id}/answer`, fd)).data;
    },
    onSuccess: () => {
      toast.success(t("rfis.toastAnswered"));
      setAnswerRfi(null);
      setAnswerForm({ answer: "", file: null });
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const closeMut = useMutation({
    mutationFn: async () => api.put(`/rfis/${closeRfiTarget.id}/close`, { reason: closeReason }),
    onSuccess: () => {
      toast.success(t("rfis.toastClosed"));
      setCloseRfiTarget(null);
      setCloseReason("");
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const editRfiMut = useMutation({
    mutationFn: async () => (await api.put(`/rfis/${editRfi.id}`, { ...editRfiForm, due_date: editRfiForm.due_date || null })).data,
    onSuccess: () => {
      toast.success(t("rfis.toastUpdated"));
      setEditRfi(null);
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const deleteRfiMut = useMutation({
    mutationFn: async (id) => api.delete(`/rfis/${id}`),
    onSuccess: () => {
      toast.success(t("rfis.toastDeleted"));
      invalidate();
    },
    onError: (e) => toast.error(fmtErr(e)),
  });

  const filtered = useMemo(
    () => (filter === "all" ? rfis : rfis.filter((r) => r.status === filter)),
    [rfis, filter]
  );

  return (
    <div className="space-y-6" data-testid="rfis-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight">{t("rfis.title")}</h1>
          <p className="text-sm text-slate-500">{t("rfis.subtitle")}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="new-rfi-button">
          <Plus className="mr-2 h-4 w-4" /> {t("rfis.newRfi")}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="rfi-filters">
        {["all", "open", "answered", "closed"].map((f) => (
          <Button key={f} variant={filter === f ? "default" : "outline"} size="sm" onClick={() => setFilter(f)} className={filter === f ? "bg-slate-900" : ""} data-testid={`rfi-filter-${f}`}>
            {t(`rfis.filter${f[0].toUpperCase() + f.slice(1)}`)}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("rfis.thNo")}</TableHead>
                <TableHead>{t("common.subject")}</TableHead>
                <TableHead>{t("rfis.thFromTo")}</TableHead>
                <TableHead>{t("rfis.thPriority")}</TableHead>
                <TableHead>{t("common.dueDate")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-500">Loading…</TableCell></TableRow>}
              {!isLoading && filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-500" data-testid="rfis-empty">No RFIs.</TableCell></TableRow>
              )}
              {filtered.map((r) => (
                <TableRow key={r.id} className={r.overdue ? "bg-rose-50/50" : ""} data-testid={`rfi-row-${r.id}`}>
                  <TableCell><span className="font-mono text-xs font-semibold" data-testid={`rfi-no-${r.id}`}>{r.rfi_no}</span></TableCell>
                  <TableCell>
                    <button type="button" className="text-left font-medium text-slate-900 transition-colors hover:text-sky-700 hover:underline" onClick={() => setViewRfi(r)} data-testid={`rfi-subject-link-${r.id}`}>
                      {r.subject}
                    </button>
                  </TableCell>
                  <TableCell className="text-sm">
                    <span className="font-medium">{r.from_party?.company_name}</span>
                    <span className="mx-1 text-slate-400">→</span>
                    <span className="font-medium">{r.to_party?.company_name}</span>
                  </TableCell>
                  <TableCell><PriorityBadge priority={r.priority} data-testid={`rfi-priority-${r.id}`} /></TableCell>
                  <TableCell>
                    <span className={`font-mono text-xs ${r.overdue ? "font-bold text-rose-700" : "text-slate-600"}`}>{fmtDate(r.due_date)}</span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <StatusBadge status={r.status} data-testid={`rfi-status-${r.id}`} />
                      {r.overdue && <StatusBadge status="overdue" />}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setViewRfi(r)} data-testid={`rfi-view-${r.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      {r.can_answer && r.status !== "closed" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-sky-700"
                          onClick={() => {
                            setAnswerRfi(r);
                            setAnswerForm({ answer: r.answer || "", file: null });
                          }}
                          data-testid={`rfi-answer-${r.id}`}
                        >
                          <MessageSquareReply className="h-4 w-4" />
                        </Button>
                      )}
                      {r.can_edit && (
                        <Button variant="ghost" size="icon" title={t("rfis.editT")} onClick={() => { setEditRfi(r); setEditRfiForm({ subject: r.subject, question: r.question, priority: r.priority, due_date: r.due_date || "" }); }} data-testid={`rfi-edit-${r.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {r.can_delete && (
                        <Button variant="ghost" size="icon" title={t("rfis.deleteT")} className="text-rose-600" onClick={() => askConfirm(t("rfis.deleteConfirm", { no: r.rfi_no }), () => deleteRfiMut.mutate(r.id))} data-testid={`rfi-delete-${r.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                      {r.can_close && (
                        <Button variant="ghost" size="icon" title={t("rfis.closeT")} className="text-emerald-700" onClick={() => { setCloseRfiTarget(r); setCloseReason(""); }} data-testid={`rfi-close-${r.id}`}>
                          <CheckCircle2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="create-rfi-dialog">
          <DialogHeader><DialogTitle>{t("rfis.createTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("rfis.addressedTo")}</Label>
              <Select value={form.to_party_id} onValueChange={(v) => setForm({ ...form, to_party_id: v })}>
                <SelectTrigger data-testid="rfi-to-select"><SelectValue placeholder={t("common.selectParty")} /></SelectTrigger>
                <SelectContent>
                  {parties.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`rfi-to-${p.id}`}>{p.company_name} ({p.role})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("common.subject")}</Label>
              <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="rfi-subject-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("rfis.questionL")}</Label>
              <RichTextEditor value={form.question} onChange={(v) => setForm({ ...form, question: v })} testId="rfi-question-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("rfis.attachmentOpt")}</Label>
              <input type="file" className="block w-full text-xs text-slate-600" onChange={(e) => setCreateFile(e.target.files?.[0] || null)} data-testid="rfi-create-file-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t("rfis.thPriority")}</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="rfi-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RFI_PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p} data-testid={`rfi-priority-${p}`}>{t(`badges.${p.toLowerCase()}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("common.dueDate")}</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="rfi-due-date-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => createMut.mutate()} disabled={!form.subject.trim() || !form.question.trim() || !form.to_party_id || createMut.isPending} data-testid="create-rfi-submit-button">
              {createMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("rfis.submitRfi")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewRfi} onOpenChange={(o) => !o && setViewRfi(null)}>
        <DialogContent data-testid="view-rfi-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="font-mono text-sm">{viewRfi?.rfi_no}</span> {viewRfi?.subject}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div>
              <div className="font-mono text-xs uppercase tracking-wider text-slate-500">{t("rfis.questionL")}</div>
              <RichTextView html={viewRfi?.question} className="mt-1 text-slate-800" />
              {viewRfi?.question_document_id && (
                <Button variant="outline" size="sm" className="mt-2" onClick={() => downloadDocument(viewRfi.question_document_id)} data-testid="rfi-question-download">
                  <Download className="mr-1.5 h-3.5 w-3.5" /> {t("rfis.questionAttachment")}
                </Button>
              )}
            </div>
            <div>
              <div className="font-mono text-xs uppercase tracking-wider text-slate-500">{t("rfis.answerL")}</div>
              {viewRfi?.answer ? (
                <>
                  <RichTextView html={viewRfi.answer} className="mt-1 text-slate-800" />
                  <p className="mt-1 text-xs text-slate-500">{viewRfi.answered_by_email} · {fmtDateTime(viewRfi.answered_at)}</p>
                  {viewRfi.answer_document_id && (
                    <Button variant="outline" size="sm" className="mt-2" onClick={() => downloadDocument(viewRfi.answer_document_id)} data-testid="rfi-answer-download">
                      <Download className="mr-1.5 h-3.5 w-3.5" /> {t("rfis.answerAttachment")}
                    </Button>
                  )}
                </>
              ) : (
                <p className="mt-1 italic text-slate-500">{t("rfis.notAnswered")}</p>
              )}
            </div>
            <ActivityTimeline
              testId="rfi-history"
              title={t("common.history")}
              events={viewRfi ? [
                { kind: "created", label: t("common.evCreated"), by: viewRfi.created_by_email, at: viewRfi.created_at },
                ...(viewRfi.updated_at ? [{ kind: "edited", label: t("common.evEdited"), by: viewRfi.updated_by, at: viewRfi.updated_at }] : []),
                ...(viewRfi.answered_at ? [{ kind: "answered", label: t("common.evAnswered"), by: viewRfi.answered_by_email, at: viewRfi.answered_at }] : []),
                ...(viewRfi.closed_at ? [{ kind: "closed", label: t("common.evClosed"), by: viewRfi.closed_by, at: viewRfi.closed_at, detail: viewRfi.close_reason }] : []),
              ] : []}
            />
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!answerRfi} onOpenChange={(o) => !o && setAnswerRfi(null)}>
        <DialogContent data-testid="answer-rfi-dialog">
          <DialogHeader><DialogTitle>{t("rfis.answerTitle", { no: answerRfi?.rfi_no })} — {answerRfi?.subject}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <RichTextView html={answerRfi?.question} className="rounded-md border bg-slate-50 p-3 text-sm text-slate-700" />
            <div className="space-y-1.5">
              <Label>{t("rfis.answerL")}</Label>
              <RichTextEditor value={answerForm.answer} onChange={(v) => setAnswerForm({ ...answerForm, answer: v })} testId="rfi-answer-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("rfis.attachmentOpt")}</Label>
              <Input type="file" onChange={(e) => setAnswerForm({ ...answerForm, file: e.target.files[0] })} data-testid="rfi-answer-file-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => answerMut.mutate()} disabled={!answerForm.answer.trim() || answerMut.isPending} data-testid="rfi-answer-submit-button">
              {answerMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("rfis.submitAnswer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editRfi} onOpenChange={(o) => !o && setEditRfi(null)}>
        <DialogContent data-testid="edit-rfi-dialog">
          <DialogHeader><DialogTitle>{t("rfis.editTitle", { no: editRfi?.rfi_no })}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>{t("common.subject")}</Label>
              <Input value={editRfiForm.subject} onChange={(e) => setEditRfiForm({ ...editRfiForm, subject: e.target.value })} data-testid="edit-rfi-subject-input" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("rfis.questionL")}</Label>
              <RichTextEditor value={editRfiForm.question} onChange={(v) => setEditRfiForm({ ...editRfiForm, question: v })} testId="edit-rfi-question-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{t("rfis.thPriority")}</Label>
                <Select value={editRfiForm.priority} onValueChange={(v) => setEditRfiForm({ ...editRfiForm, priority: v })}>
                  <SelectTrigger data-testid="edit-rfi-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {RFI_PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p}>{t(`badges.${p.toLowerCase()}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("common.dueDate")}</Label>
                <Input type="date" value={editRfiForm.due_date} onChange={(e) => setEditRfiForm({ ...editRfiForm, due_date: e.target.value })} data-testid="edit-rfi-due-date-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => editRfiMut.mutate()} disabled={!editRfiForm.subject.trim() || !editRfiForm.question.trim() || editRfiMut.isPending} data-testid="edit-rfi-submit-button">
              {editRfiMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("common.saveChanges")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!closeRfiTarget} onOpenChange={(o) => !o && setCloseRfiTarget(null)}>
        <DialogContent data-testid="close-rfi-dialog">
          <DialogHeader><DialogTitle>{t("rfis.closeTitle", { no: closeRfiTarget?.rfi_no })} — {closeRfiTarget?.subject}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">{t("rfis.closeNote")}</p>
            <div className="space-y-1.5">
              <Label>{t("filesPage.reasonL")}</Label>
              <Textarea value={closeReason} onChange={(e) => setCloseReason(e.target.value)} placeholder={t("rfis.reasonPh")} data-testid="close-rfi-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => closeMut.mutate()} disabled={closeMut.isPending} data-testid="close-rfi-submit-button">
              {closeMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("rfis.closeSubmit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {confirmDialog}
    </div>
  );
}
